import { dropTask } from 'ember-concurrency';
import * as v from 'valibot';
import {
  pslfEstimatorSchema,
  idrEstimatorSchema,
} from 'tio-common/validation-schema/forms/loan-forgiveness-estimator';
import { hash } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import { Input, RadioGroup, Select, Form } from 'tio-ui/components/forms';
import type { FormResultData } from 'tio-ui/components/forms';
import inputmask from 'tio-common/modifiers/inputmask';
import { Popover } from 'tio-ui/components/popover';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type AnswerProfileService from 'tio-employee/services/answer-profile';
import type IntlService from 'ember-intl/services/intl';
import type RecommendationModel from 'tio-common/models/recommendation';
import type SessionContextService from 'tio-employee/services/session-context';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import Button from 'tio-reimagined/components/button';
import { on } from '@ember/modifier';
import { VStack } from 'tio-ui/components/layout';
import { Text } from 'tio-ui/components/typography';

type FormData = {
  householdIncome: string | number;
  customIncome: string;
  householdSize?: string;
  totalYears?: string | number;
  before2007: boolean;
  academicDegree: string;
  affordPayments: string;
};

type FormModel = {
  federalLoanTime?: string;
  householdIncome: number;
  householdSize: number;
  academicDegree: string;
  madePayments?: number;
  affordPayments?: string;
};

export interface ForgivenessEstimatorFormSignature {
  Args: {
    loanSummary: LoanSummary;
    application: 'IDR' | 'PSLF';
    didSave: (recommendation: RecommendationModel) => void;
  };
  Element: HTMLElement;
}

class ForgivenessEstimatorFormComponent extends Component<ForgivenessEstimatorFormSignature> {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare answerProfile: AnswerProfileService;

  @tracked formData: FormData = {
    householdIncome: '',
    customIncome: '0',
    before2007: false,
    academicDegree: '',
    affordPayments: '',
  };

  @tracked showCustomIncome = false;
  @tracked hasSubmitted = false;
  @tracked submitError = null;
  @tracked errors: Record<string, string[]> = {};

  get loanSummariesByType() {
    return this.args.loanSummary?.summaryByLoanType || [];
  }

  get showUploadNslds() {
    return this.loanSummariesByType.length === 0;
  }

  get source() {
    return this.isPslf ? 'pslf' : 'idr';
  }

  get isPslf() {
    return this.args.application === 'PSLF';
  }

  get isIdr() {
    return this.args.application === 'IDR';
  }

  get dataStyle() {
    return this.isPslf ? 'PSLFv2' : 'IDR';
  }

  get validationSchema() {
    if (this.isPslf) {
      return pslfEstimatorSchema;
    }
    return idrEstimatorSchema;
  }

  get federalLoanTime() {
    if (this.formData.before2007 === undefined) {
      return this.formData.before2007;
    }
    return this.formData.before2007 ? 'before-october-2007' : 'after-october-2007';
  }

  get madePayments(): number | undefined {
    const val = this.formData.totalYears;
    if (val === undefined || val === null) return undefined;
    if (typeof val === 'string' && val.trim() === '') return undefined;
    const num = Number(val);
    if (isNaN(num)) return undefined;
    return num * 12;
  }

  handleFormChange = (result: FormResultData<FormData>) => {
    const newData = { ...result.data };

    // Fix boolean radios coming as strings
    if (typeof newData.before2007 === 'string') {
      newData.before2007 = newData.before2007 === 'true';
    }

    this.formData = newData;
    this.showCustomIncome = this.formData.householdIncome === 'Custom';
    this.validate();
  };

  submitTask = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;
    if (!this.validate()) {
      return;
    }
    try {
      const saved = await this.saveFormValues.perform(this.formModel);
      this.hasSubmitted = false;
      await this.args.didSave?.(saved);
    } catch (e) {
      this.submitError = e;
      console.error(e);
    }
  });

  saveFormValues = dropTask(async (formModel) => {
    const app = this.args.application;
    const calculationInput = this.formatPayload(formModel);
    const recommendation = await this.store.createRecord<RecommendationModel>('recommendation', {
      user: this.sessionContext.user,
      calculationInput: { data: calculationInput },
      requestingApp: app,
    });
    this.storeAnswersOnProfile(formModel);
    await recommendation.save();
    return recommendation;
  });

  storeAnswersOnProfile(formModel: unknown) {
    // We don't need this method to be awaited in the context of the estimator flow.
    // The answerProfile service will persist and finish this request, so as not
    // to interfere with the wait time or overall user experience of the estimator.
    try {
      const app = this.args.application;
      // @ts-expect-error: not sure
      this.answerProfile.storeAnswers(app, formModel);
    } catch (e) {
      // Catch and log but don't re-throw so as not to block estimator experience.
      console.error(e);
    }
  }

  formatPayload(formModel: FormModel) {
    const { dataStyle } = this;
    const {
      academicDegree,
      householdIncome,
      householdSize,
      madePayments,
      federalLoanTime,
      affordPayments,
    } = formModel;

    return this.loanSummariesByType.map((loanData) => {
      const {
        loanType,
        totalDisbursedAmount,
        repaymentPlan,
        /*federalLoanTime,*/
        currentBalance,
        interestRate,
        monthlyPayment,
      } = loanData;

      const formatted = {
        'data-style': dataStyle,
        'loan-type': {
          'federal-loan-type': {
            'loan-type': loanType,
            'original-disbursement-amount': totalDisbursedAmount,
            'repayment-plan': repaymentPlan,
            'monthly-payment': monthlyPayment,
          },
          // Check if this should be removed from form in favor of loan summary federalLoanTime data? - Julia 01/23/2023
          'federal-loan-time': federalLoanTime,
        },
        forgiveness: {
          'made-payments': madePayments,
          'academic-degree': academicDegree,
        },
        'loan-details': {
          'household-income': householdIncome,
          'current-balance': currentBalance,
          'interest-rate': interestRate,
          'household-size': householdSize,
        },
        purpose: {
          'afford-payments': affordPayments,
        },
      };

      return formatted;
    });
  }

  grossIncomes = [
    { label: 'Less than 25K', key: 2500000 },
    { label: '25K - 30K', key: 2750000 },
    { label: '30K - 40K', key: 3500000 },
    { label: '40K - 50K', key: 4500000 },
    { label: '50K - 60K', key: 5500000 },
    { label: '60K - 70K', key: 6500000 },
    { label: '70K - 80K', key: 7500000 },
    { label: '80K - 100K', key: 9000000 },
    { label: '100K - 125K', key: 11250000 },
    { label: '125K - 150K', key: 13750000 },
    { label: '150K - 200K', key: 17500000 },
    { label: '200K - 250K', key: 22500000 },
    { label: '250K - 350K', key: 27500000 },
    { label: '350K - 500K', key: 42500000 },
    { label: 'More than 500K', key: 50000000 },
    { label: 'Custom', key: 'Custom' },
  ];

  yearOptions = [
    { label: "I haven't started", key: '0' },
    { label: '1 year', key: '1' },
    { label: '2 years', key: '2' },
    { label: '3 years', key: '3' },
    { label: '4 years', key: '4' },
    { label: '5 years', key: '5' },
    { label: '6 years', key: '6' },
    { label: '7 years', key: '7' },
    { label: '8 years', key: '8' },
    { label: '9 years', key: '9' },
    { label: '10 years', key: '10' },
  ];

  get formModel(): FormModel {
    let income = 0;
    if (this.formData.householdIncome === 'Custom') {
      const clean = this.formData.customIncome.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(clean);
      income = isNaN(parsed) ? 0 : parsed * 100;
    } else {
      income = Number(this.formData.householdIncome);
    }

    const baseModel = {
      academicDegree: this.formData.academicDegree,
      federalLoanTime: this.federalLoanTime as string | undefined,
      householdIncome: income,
      householdSize: parseInt(this.formData.householdSize || ''),
      madePayments: this.madePayments,
    };

    if (this.isIdr) {
      return {
        ...baseModel,
        affordPayments: this.formData.affordPayments,
      };
    }

    return baseModel;
  }

  validate = () => {
    const schema = this.validationSchema;
    const result = v.safeParse(schema, this.formModel);

    if (result.success) {
      this.errors = {};
      return true;
    }

    const newErrors: Record<string, string[]> = {};
    for (const issue of result.issues) {
      const path = issue.path?.[0]?.key as string;
      if (path) {
        if (!newErrors[path]) {
          newErrors[path] = [];
        }
        newErrors[path]?.push(issue.message);
      }
    }
    this.errors = newErrors;
    return false;
  };

  <template>
    {{#if this.showUploadNslds}}
      <VStack class="text-center items-center">
        <div class="text-left max-w-lg">
          <Text @tag="p" class="mb-4">
            {{t "strategy_finder.missing_loan_types.message"}}
          </Text>
          <Text @tag="p" class="mb-6">
            {{t "strategy_finder.missing_loan_types.upload_prompt"}}
          </Text>
        </div>
        <Button
          @intent="primary"
          {{on
            "click"
            (transitionTo
              "authenticated.observability.upload"
              (hash queryParams=(hash source=this.source providers="nslds"))
            )
          }}
        >
          {{t "strategy_finder.missing_loan_types.upload_button"}}
        </Button>
      </VStack>
    {{else}}
      <Form
        @data={{this.formData}}
        @onChange={{this.handleFormChange}}
        @onSubmit={{this.submitTask.perform}}
        ...attributes
      >
        <VStack>
          {{! Household Income }}
          <Popover as |pop|>
            <span
              {{pop.trigger}}
              {{pop.anchor}}
              {{on "mouseenter" pop.open}}
              {{on "mouseleave" pop.close}}
            >
              <label for="householdIncome" class="form-field-label">
                {{t "forgiveness_estimator.annual_income"}}
              </label>
            </span>
            <pop.Content @class="p-2">
              {{t "forgiveness_estimator.annual_income_tooltip"}}
            </pop.Content>
          </Popover>
          <Select
            data-test-household-income
            @name="householdIncome"
            @isClearable={{false}}
            @errors={{errorsForValibotField
              "householdIncome"
              schemaErrors=this.errors
              hasSubmitted=this.hasSubmitted
            }}
            @items={{this.grossIncomes}}
            @isRequired={{true}}
          >
            <:item as |o|>
              <o.Item @key={{o.key}}>
                {{o.label}}
              </o.Item>
            </:item>
          </Select>

          {{! Custom Income Amount }}
          {{#if this.showCustomIncome}}
            <Input
              data-test-custom-income
              id="custom-inc"
              @name="customIncome"
              autocomplete="off"
              {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
            />
          {{/if}}

          {{! Household Size }}
          <Input
            data-test-household-size
            @name="householdSize"
            @label={{t "forgiveness_estimator.family_size"}}
            @errors={{errorsForValibotField
              "householdSize"
              schemaErrors=this.errors
              hasSubmitted=this.hasSubmitted
            }}
            @isRequired={{true}}
            @type="number"
            autocomplete="off"
            min="1"
          />

          {{! Total Years Paid }}
          <Popover as |pop|>
            <span
              {{pop.trigger}}
              {{pop.anchor}}
              {{on "mouseenter" pop.open}}
              {{on "mouseleave" pop.close}}
            >
              <label for="totalYears" class="form-field-label">
                {{t "forgiveness_estimator.how_many_years"}}
              </label>
            </span>
            <pop.Content>
              {{#if this.isPslf}}
                {{t "forgiveness_estimator.how_many_years_tooltip"}}
              {{/if}}
            </pop.Content>
          </Popover>
          <Select
            data-test-total-years
            @name="totalYears"
            @isClearable={{true}}
            @errors={{errorsForValibotField
              "madePayments"
              schemaErrors=this.errors
              hasSubmitted=this.hasSubmitted
            }}
            @items={{this.yearOptions}}
            @isRequired={{true}}
          >
            <:item as |o|>
              <o.Item @key={{o.key}}>
                {{o.label}}
              </o.Item>
            </:item>
          </Select>

          {{! Federal Loan Time }}
          <RadioGroup
            data-test-federal-loan-time
            @name="before2007"
            @label={{t "forgiveness_estimator.before_2007"}}
            @errors={{errorsForValibotField
              "federalLoanTime"
              schemaErrors=this.errors
              hasSubmitted=this.hasSubmitted
            }}
            @value={{this.formData.before2007}}
            @isRequired={{true}}
            as |Radio|
          >
            <Radio @value={{true}} @label={{t "common.yes"}} />
            <Radio @value={{false}} @label={{t "common.no"}} />
          </RadioGroup>

          {{! Academic degree }}
          <RadioGroup
            data-test-academic-degree
            @name="academicDegree"
            @label={{t "forgiveness_estimator.grad_or_undergrad"}}
            @errors={{errorsForValibotField
              "academicDegree"
              schemaErrors=this.errors
              hasSubmitted=this.hasSubmitted
            }}
            @value={{this.formData.academicDegree}}
            @isRequired={{true}}
            as |Radio|
          >
            <Radio @value="undergradOnly" @label={{t "forgiveness_estimator.undergradOnly"}} />
            <Radio @value="GradOnly" @label={{t "forgiveness_estimator.GradOnly"}} />
            <Radio @value="undergradGrad" @label={{t "forgiveness_estimator.undergradGrad"}} />
          </RadioGroup>

          {{#if this.isIdr}}
            {{! Afford Payments }}
            <RadioGroup
              data-test-afford-payments
              @name="affordPayments"
              @label="{{t 'forgiveness_estimator.afford_payments'}}"
              @errors={{errorsForValibotField
                "affordPayments"
                schemaErrors=this.errors
                hasSubmitted=this.hasSubmitted
              }}
              @value={{this.formData.affordPayments}}
              @isRequired={{true}}
              as |Radio|
            >
              <Radio @value="yes" @label={{t "common.yes"}} />
              <Radio @value="no" @label={{t "common.no"}} />
            </RadioGroup>
          {{/if}}
          <div>
            <Button
              data-test-see-results
              @intent="primary"
              @type="submit"
              @isRunning={{this.submitTask.isRunning}}
            >
              {{t "forgiveness_estimator.see_results"}}
            </Button>
          </div>
        </VStack>
      </Form>
    {{/if}}
  </template>
}

export default ForgivenessEstimatorFormComponent;
